import { ApplicationElement } from '@crossiety/lit-components';

/**
 * Component for formatting the range between 2 dates (eg. Oct 10, 2025 - Oct 11, 2025).
 * - The date shown will always have a day, month and a year (eg. Oct 10 2025).
 * - `timeOn` will show the time of the day (eg. Oct 10, 2025 **20:20**).
 * - `weekOn` will show the day of the week (eg. **Mon**, Oct 10 2025).
 * - If the start and end date are the same, only one will be shown (to avoid for eg. Oct 10 2025 - Oct 10 2025).
 * - If the start and end date are the same and the `timeOn = true`, the `to` day will be shown only as a time (eg. Oct 10 2025 20:20 - **21:20**).
 *
 * @prop from - Starting date
 * @prop to - Ending date
 * @prop weekOn - Should show day of the week?
 * @prop timeOn - Should show time of the day?
 */

export default class TimeRange extends ApplicationElement {
  static properties = {
    from: { type: Date },
    to: { type: Date },
    timeOn: { type: Boolean },
    weekOn: { type: Boolean },
  };

  /** @internal */
  get isSameDay() {
    return (
      this.from.getFullYear() === this.to.getFullYear() &&
      this.from.getMonth() === this.to.getMonth() &&
      this.from.getDate() === this.to.getDate()
    );
  }

  /** @internal */
  get isSameTime() {
    return (
      this.from.getHours() === this.to.getHours() &&
      this.from.getMinutes() === this.to.getMinutes()
    );
  }

  get showOnlyOneDate() {
    return this.isSameDay && (!this.timeOn || (this.timeOn && this.isSameTime));
  }

  /** @internal */
  get format() {
    if (this.weekOn && this.timeOn) {
      return 'time.formats.custom1';
    }

    if (this.weekOn) {
      return 'date.formats.custom1';
    }

    if (this.timeOn) {
      return 'time.formats.medium';
    }

    return 'date.formats.medium';
  }

  /** @internal */
  get fromStr() {
    return this.i18n.l(this.format, this.from);
  }

  /** @internal */
  get toStr() {
    return this.isSameDay && this.timeOn
      ? this.i18n.l('time.formats.compact', this.to)
      : this.i18n.l(this.format, this.to);
  }

  render() {
    return this.showOnlyOneDate
      ? this.fromStr
      : `${this.fromStr} - ${this.toStr}`;
  }
}

customElements.get('xs-time-range') ||
  customElements.define('xs-time-range', TimeRange);
